import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid,
  Chip,
  InputAdornment,
  MenuItem,
  Alert,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../providers/TenantProvider.js';
import { db, functions } from '../firebase.js';
import { httpsCallable } from 'firebase/functions';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  increment,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import type { Member, MemberStatus } from '../lib/db/types.js';

interface MemberWithId extends Member {
  id: string;
}

export function MembersListPage() {
  const { tenantId, role, user, activeTenant } = useTenant();
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberWithId[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<MemberWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus | 'all'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    barcodeId: '',
    portalPassword: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [checkingInMemberId, setCheckingInMemberId] = useState<string | null>(null);
  const [checkingWaiver, setCheckingWaiver] = useState(false);
  const [waiverStatus, setWaiverStatus] = useState<{ signed: boolean; signedAt?: string | null } | null>(null);

  const canEdit = role === 'owner' || role === 'manager';

  // Load members
  useEffect(() => {
    if (!tenantId) return;

    const loadMembers = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'members'),
          where('tenantId', '==', tenantId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const membersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MemberWithId[];
        setMembers(membersData);
        setFilteredMembers(membersData);
      } catch (err) {
        console.error('Error loading members:', err);
        setError('Failed to load members');
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [tenantId]);

  // Filter members
  useEffect(() => {
    let filtered = members;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.contact.firstName.toLowerCase().includes(term) ||
          m.contact.lastName.toLowerCase().includes(term) ||
          m.contact.email.toLowerCase().includes(term) ||
          m.contact.phone?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((m) => m.status === statusFilter);
    }

    setFilteredMembers(filtered);
  }, [searchTerm, statusFilter, members]);

  useEffect(() => {
    if (!createDialogOpen || !tenantId) {
      setCheckingWaiver(false);
      setWaiverStatus(null);
      return;
    }

    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setCheckingWaiver(false);
      setWaiverStatus(null);
      return;
    }

    let cancelled = false;
    setCheckingWaiver(true);

    const timer = setTimeout(async () => {
      try {
        const snapshot = await getDoc(doc(db, 'tenants', tenantId, 'waiverKeys', `email_${email}`));
        if (cancelled) return;

        if (!snapshot.exists()) {
          setWaiverStatus({ signed: false });
          return;
        }

        const data = snapshot.data() as { latestSignedAt?: { toDate?: () => Date } };
        setWaiverStatus({
          signed: true,
          signedAt: data.latestSignedAt?.toDate?.()?.toLocaleString?.() || null,
        });
      } catch (lookupError) {
        console.error('Error looking up waiver status:', lookupError);
        if (!cancelled) {
          setWaiverStatus(null);
        }
      } finally {
        if (!cancelled) {
          setCheckingWaiver(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [createDialogOpen, formData.email, tenantId]);

  const handleCreateMember = async () => {
    if (!tenantId) return;
    if (!waiverStatus?.signed) {
      setError('A signed waiver is required before creating this member');
      return;
    }

    try {
      // Create member data with generated barcode or use provided one
      const barcodeId = formData.barcodeId.trim() || 
        `GYM-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      const createMember = httpsCallable(functions, 'createMember');
      const result = await createMember({
        tenantId,
        barcodeId,
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || undefined,
        },
        status: 'active',
        portalPassword: formData.portalPassword || undefined,
      });
      const data = result.data as { memberId: string };

      // Add to local state
      const newMember: MemberWithId = {
        id: data.memberId,
        tenantId,
        barcodeId,
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone || undefined,
        },
        emailLower: formData.email.trim().toLowerCase(),
        status: 'active',
        attendanceCount: 0,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
        joinedAt: new Date() as any,
      };
      setMembers([newMember, ...members]);

      setSuccess('Member created successfully');
      setCreateDialogOpen(false);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', barcodeId: '', portalPassword: '' });
      setWaiverStatus(null);
    } catch (err) {
      console.error('Error creating member:', err);
      setError('Failed to create member');
    }
  };

  const handleCheckInMember = async (member: MemberWithId) => {
    if (!tenantId) return;

    if (member.status !== 'active') {
      setError(`Cannot check in a member with "${member.status}" status`);
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setCheckingInMemberId(member.id);

      await addDoc(collection(db, 'checkins'), {
        tenantId,
        memberId: member.id,
        locationId: 'default',
        timestamp: serverTimestamp(),
        method: 'manual',
        verifiedBy: user?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, 'members', member.id), {
        attendanceCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      setMembers((currentMembers) =>
        currentMembers.map((currentMember) =>
          currentMember.id === member.id
            ? {
                ...currentMember,
                attendanceCount: (currentMember.attendanceCount || 0) + 1,
                updatedAt: new Date() as any,
              }
            : currentMember
        )
      );
      setSuccess(`Checked in ${member.contact.firstName} ${member.contact.lastName}`);
    } catch (err) {
      console.error('Error recording check-in:', err);
      setError('Failed to record check-in');
    } finally {
      setCheckingInMemberId(null);
    }
  };

  const getStatusColor = (status: MemberStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading members...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Members
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Member
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          placeholder="Search members..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setStatusFilter(e.target.value as MemberStatus | 'all')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="canceled">Canceled</MenuItem>
        </TextField>
      </Box>

      {/* Members List */}
      {filteredMembers.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              No members found
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredMembers.map((member) => (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  transition: 'box-shadow 0.2s',
                }}
                onClick={() => navigate(`/members/${member.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">
                      {member.contact.firstName} {member.contact.lastName}
                    </Typography>
                    <Chip
                      label={member.status}
                      size="small"
                      color={getStatusColor(member.status)}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {member.contact.email}
                  </Typography>
                  {member.contact.phone && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {member.contact.phone}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      Check-ins: {member.attendanceCount || 0}
                    </Typography>
                    {member.joinedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Joined: {new Date(member.joinedAt.seconds * 1000).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleCheckInMember(member);
                    }}
                    disabled={checkingInMemberId === member.id || member.status !== 'active'}
                  >
                    {checkingInMemberId === member.id ? 'Checking In...' : 'Check In'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Member Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, email: e.target.value })}
            />
            {checkingWaiver && (
              <Alert severity="info">Checking waiver status...</Alert>
            )}
            {!checkingWaiver && waiverStatus?.signed && (
              <Alert severity="success">
                Signed waiver on {waiverStatus.signedAt || 'a previous date'}.
              </Alert>
            )}
            {!checkingWaiver && formData.email.trim() && waiverStatus && !waiverStatus.signed && (
              <Alert
                severity="warning"
                action={
                  activeTenant?.slug ? (
                    <Button color="inherit" size="small" onClick={() => navigate('/waivers')}>
                      Open Waivers
                    </Button>
                  ) : undefined
                }
              >
                No signed waiver is on file for this email yet.
              </Alert>
            )}
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              label="Barcode ID (optional)"
              placeholder="GYM-1234567890-ABC123"
              value={formData.barcodeId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, barcodeId: e.target.value })}
              helperText="Leave empty to auto-generate"
            />
            <TextField
              label="Portal Password (optional)"
              type="password"
              value={formData.portalPassword}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, portalPassword: e.target.value })}
              helperText="Set this to let the member sign in and switch tenants"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateMember}
            disabled={!formData.firstName || !formData.lastName || !formData.email || checkingWaiver || !waiverStatus?.signed}
          >
            Create Member
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
